import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The ONE token-resolve prelude for tokenized hosted pages (arch W3 / F3).
 *
 * Every public hosted page (`/e/:employerToken` employee flow,
 * `/contract/:contractToken` signing page, and any future tokenized surface)
 * starts the same way: take the caller-supplied token, resolve it against a
 * public endpoint, and discriminate three outcomes before any page-specific
 * state exists. This hook owns that prelude — and the convention every public
 * resolver must honor to plug into it:
 *
 *   resolves to the resource — the token is good            → phase 'ready'
 *   resolves to `null`       — unknown/invalid token (400/404) → 'invalid'
 *   throws                   — transport/server outage       → 'outage'
 *
 * The invalid-vs-outage distinction is policy, not plumbing: a backend blip
 * must NEVER read as "this link is invalid" to the person holding a valid
 * emailed link — outage gets a retry affordance instead. Resolvers implement
 * the convention with `isHttpStatus` below (return null on 400/404, rethrow
 * the rest); see `getPublicLink` / `getContract` for the two live adapters.
 *
 * Pages keep everything AFTER the resolve (auth/enroll phases, sign flow, …)
 * to themselves — only the shared prelude lives here.
 */

export type PublicTokenPhase = 'loading' | 'invalid' | 'outage' | 'ready';

export interface PublicTokenResource<T> {
  phase: PublicTokenPhase;
  /** The resolved resource — non-null exactly when `phase` is 'ready'. */
  data: T | null;
  /** The token this resource was resolved for (`''` → 'invalid', no fetch). */
  token: string;
  /** Re-run the resolve — the outage screen's "Try again". */
  retry: () => void;
  /**
   * Re-resolve after the token's own state moved mid-flow (e.g. a sign POST
   * 409/404: signed elsewhere, expired, rotated) so the page lands on the
   * truthful terminal state instead of an inline error.
   */
  resolveAgain: () => Promise<void>;
}

/**
 * `fetchResource` must be referentially stable (a module-level function like
 * `getContract`, or memoized) — it is a dependency of the resolve effect, so a
 * new identity per render would re-resolve in a loop.
 */
export function usePublicTokenResource<T>(
  fetchResource: (token: string) => Promise<T | null>,
  /**
   * The public token to resolve — supplied by the caller (a route param like
   * `useParams().employerToken` / `contractToken`, or a legacy `?t=` read).
   * `''` → phase 'invalid' with no fetch, unchanged policy.
   */
  token: string
): PublicTokenResource<T> {
  const [phase, setPhase] = useState<PublicTokenPhase>('loading');
  const [data, setData] = useState<T | null>(null);
  // Latest-only guard: a retry (or token change) supersedes any in-flight
  // resolve, so a stale response can never overwrite a newer phase.
  const generation = useRef(0);

  const resolve = useCallback(async () => {
    const gen = ++generation.current;
    if (!token) {
      setPhase('invalid');
      return;
    }
    setPhase('loading');
    try {
      const resource = await fetchResource(token);
      if (gen !== generation.current) return;
      if (!resource) {
        setPhase('invalid');
        return;
      }
      setData(resource);
      setPhase('ready');
    } catch {
      if (gen !== generation.current) return;
      setPhase('outage');
    }
  }, [token, fetchResource]);

  useEffect(() => {
    resolve();
    return () => {
      // Unmount/re-resolve invalidates whatever is still in flight.
      generation.current++;
    };
  }, [resolve]);

  return { phase, data, token, retry: resolve, resolveAgain: resolve };
}

/** The HTTP status off an axios-style error (`undefined` for non-HTTP/transport
 * errors) — for redacted, status-only failure logs that never touch the payload
 * or token. */
export function httpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}

/**
 * Shared by the public resolvers to implement the null-vs-throw convention
 * above (and by action calls like the contract sign to spot state-moved 409s).
 */
export function isHttpStatus(err: unknown, codes: number[]): boolean {
  const status = httpStatus(err);
  return typeof status === 'number' && codes.includes(status);
}
